import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useMediaQuery } from '../lib/useMediaQuery';

type Metrics = {
  users: number;
  workspaces: number;
  apiKeys: number;
  strategyRuns: number;
  armedRuns: number;
  memoryMode: boolean;
  uptimeSec: number;
  brokerId: string | null;
};

type ClientRow = {
  id: string;
  email: string;
  planId: string;
  createdAt?: string;
  depositUsdt?: number;
  tradedVolumeUsdt?: number;
  exchangeEquityUsdt?: number | null;
  turnover?: number;
};
type ClientPeriod = '24h' | '7d' | '30d' | 'all';
type LogLine = { ts: string; level: string; message: string; channel?: string };
type EngineRun = {
  id: string;
  kind: string;
  symbol: string;
  timeframe: string;
  status: string;
  live?: boolean;
  apiKeyId?: string | null;
  workspaceId?: string | null;
};

const ADMIN_TOKEN_KEY = 'termix_admin_token';

export default function AdminPage() {
  const narrow = useMediaQuery('(max-width: 900px)');
  const [token, setToken] = useState(localStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [email, setEmail] = useState('admin@termix.local');
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [clientPeriod, setClientPeriod] = useState<ClientPeriod>('7d');
  const [clientTotals, setClientTotals] = useState({
    depositUsdt: 0, tradedVolumeUsdt: 0, exchangeEquityUsdt: 0,
  });
  const [logs, setLogs] = useState<LogLine[]>([]);
  const [logChannel, setLogChannel] = useState<'all' | 'api' | 'engine' | 'deploy'>('all');
  const [logCounts, setLogCounts] = useState({ api: 0, engine: 0, deploy: 0 });
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [engineRuns, setEngineRuns] = useState<EngineRun[]>([]);
  const [deployOut, setDeployOut] = useState('');
  const [error, setError] = useState('');
  const { t } = useI18n();
  const [updateStatus, setUpdateStatus] = useState<{
    deployedSha?: string | null;
    deployedAt?: string | null;
    deployedSrc?: string | null;
    availableSha?: string | null;
    availableRoot?: string | null;
    localBtddSha?: string | null;
    githubMain?: string | null;
    githubSource?: string | null;
    githubReachable?: boolean;
    tipLive?: boolean;
    tipHelperOk?: boolean;
    updateAvailable?: boolean;
    statusReason?: string | null;
    wrapperOk?: boolean;
    oneShot?: string;
    changelog?: Array<{ n?: number; sha: string; message: string; body?: string | null; date?: string | null }>;
    changelogSource?: string | null;
    history?: Array<{ n?: number; sha: string; message: string; body?: string | null; date?: string | null }>;
    historySource?: string | null;
    deployStatus?: { phase?: string; detail?: string; at?: string } | null;
  } | null>(null);
  const [openHistorySha, setOpenHistorySha] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const pollRef = useRef<number | undefined>(undefined);
  const [billing, setBilling] = useState<{
    settings: { treasuryAddress: string; commissionBps: number; usdtMetadata: string };
    wallets: Array<{ id: string; userId: string; email?: string; address: string; balanceUsdt: number }>;
  } | null>(null);
  const [treasuryDraft, setTreasuryDraft] = useState('');
  const [feeDraft, setFeeDraft] = useState('100');

  const adminFetch = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const headers = new Headers(init.headers || {});
    headers.set('Content-Type', 'application/json');
    headers.set('x-admin-token', token);
    headers.set('x-admin-email', email);
    const res = await fetch(path, { ...init, headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || res.statusText);
    return data as T;
  };

  const refresh = async (period = clientPeriod) => {
    const [m, c, l, s, u, b, er] = await Promise.all([
      adminFetch<Metrics>('/api/admin/metrics'),
      // Exchange equity only on explicit refresh / page load — not polled.
      adminFetch<{
        clients: ClientRow[];
        totals?: { depositUsdt: number; tradedVolumeUsdt: number; exchangeEquityUsdt?: number };
      }>(`/api/admin/clients?period=${period}&exchange=1`),
      adminFetch<{ logs: LogLine[]; channels?: { api: number; engine: number; deploy: number } }>(
        `/api/admin/logs?channel=${logChannel}`,
      ),
      adminFetch<{ settings: Record<string, string> }>('/api/admin/settings'),
      adminFetch<NonNullable<typeof updateStatus>>('/api/admin/update-status').catch(() => null),
      adminFetch<NonNullable<typeof billing>>('/api/admin/billing').catch(() => null),
      adminFetch<{ runs: EngineRun[] }>('/api/admin/engine/runs').catch(() => ({ runs: [] })),
    ]);
    setMetrics(m);
    setClients(c.clients || []);
    setClientTotals({
      depositUsdt: Number(c.totals?.depositUsdt || 0),
      tradedVolumeUsdt: Number(c.totals?.tradedVolumeUsdt || 0),
      exchangeEquityUsdt: Number(c.totals?.exchangeEquityUsdt || 0),
    });
    setLogs(l.logs || []);
    if (l.channels) setLogCounts(l.channels);
    setSettings(s.settings || {});
    if (u) setUpdateStatus(u);
    setEngineRuns(er.runs || []);
    if (b) {
      setBilling(b);
      setTreasuryDraft(b.settings.treasuryAddress || '');
      setFeeDraft(String(b.settings.commissionBps ?? 100));
    }
    return u;
  };

  useEffect(() => {
    if (!token) return;
    void refresh()
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken('');
        setAuthed(false);
        setError('Сессия админа истекла — войдите снова');
      });
  }, [token]);

  // Wheel over number/select fields: blur so #root page scroll keeps working.
  useEffect(() => {
    if (!authed) return;
    const onWheel = (e: WheelEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const field = t.closest('input, select, textarea') as HTMLInputElement | null;
      if (!field) return;
      if (field.type === 'number' || field.tagName === 'SELECT') field.blur();
    };
    const root = document.getElementById('root') || document.body;
    root.addEventListener('wheel', onWheel, { passive: true, capture: true });
    return () => root.removeEventListener('wheel', onWheel, true);
  }, [authed]);

  useEffect(() => {
    if (!authed || !token) return;
    void adminFetch<{ logs: LogLine[]; channels?: { api: number; engine: number; deploy: number } }>(
      `/api/admin/logs?channel=${logChannel}`,
    ).then((l) => {
      setLogs(l.logs || []);
      if (l.channels) setLogCounts(l.channels);
    }).catch(() => null);
  }, [logChannel, authed, token]);

  useEffect(() => () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
  }, []);

  const login = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const em = email.trim().toLowerCase();
    const pw = password;
    if (!em || !pw) {
      setError('Введите email и пароль админа');
      return;
    }
    try {
      const data = await api<{ token: string; ok: boolean }>('/api/admin/login', {
        method: 'POST',
        body: JSON.stringify({ email: em, password: pw }),
      });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setEmail(em);
      setToken(data.token);
      // refresh() via token effect; mark provisional so form flips even if slow
      setAuthed(true);
    } catch (err) {
      setError((err as Error).message || 'invalid admin credentials');
    }
  };

  const checkUpdates = async () => {
    setBusy(true);
    try {
      const u = await adminFetch<NonNullable<typeof updateStatus>>('/api/admin/update-status');
      setUpdateStatus(u);
      const phase = u.deployStatus?.phase;
      setDeployOut(
        [
          u.statusReason || '',
          phase ? `deploy: ${phase}${u.deployStatus?.detail ? ` — ${u.deployStatus.detail}` : ''}` : '',
          u.deployStatus?.at ? `at ${u.deployStatus.at}` : '',
        ].filter(Boolean).join('\n'),
      );
    } catch (err) {
      setDeployOut((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const startDeployPoll = (prevSha: string | null | undefined) => {
    setDeploying(true);
    if (pollRef.current) window.clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = window.setInterval(() => {
      ticks += 1;
      void (async () => {
        try {
          const u = await adminFetch<NonNullable<typeof updateStatus>>('/api/admin/update-status');
          setUpdateStatus(u);
          const phase = String(u.deployStatus?.phase || '');
          const detail = String(u.deployStatus?.detail || '');
          setDeployOut(`Установка… phase=${phase || '…'}${detail ? `\n${detail}` : ''}\nVPS ${u.deployedSha || '—'} · GitHub ${u.githubMain || '—'}`);
          const done = phase === 'done' || phase === 'failed'
            || (prevSha && u.deployedSha && u.deployedSha !== prevSha)
            || ticks >= 60;
          if (done) {
            if (pollRef.current) window.clearInterval(pollRef.current);
            setDeploying(false);
            setBusy(false);
            setDeployOut(
              phase === 'failed'
                ? `Деплой упал:\n${detail}`
                : `Готово · VPS ${u.deployedSha || '—'} · ${u.statusReason || 'актуально'}`,
            );
            await refresh();
          }
        } catch {
          /* API may restart mid-build */
        }
      })();
    }, 5000);
  };

  const deploy = async () => {
    setBusy(true);
    setDeployOut('Установка запущена… следим за phase (fetch → npm → build → restart → done)');
    const prevSha = updateStatus?.deployedSha;
    try {
      const data = await adminFetch<{ ok: boolean; stdout?: string; stderr?: string; message?: string; via?: string }>(
        '/api/admin/deploy',
        { method: 'POST' },
      );
      setDeployOut(`${data.via ? `via ${data.via}\n` : ''}${data.message || 'started'}`);
      startDeployPoll(prevSha);
    } catch (err) {
      setDeployOut((err as Error).message);
      setBusy(false);
      setDeploying(false);
    }
  };

  if (!authed) {
    return (
      <div style={{
        height: '100%', overflow: 'auto', display: 'grid', placeItems: 'center',
        padding: '24px 16px calc(24px + var(--safe-bottom))',
      }}>
        <form onSubmit={login} style={{
          width: 'min(380px, 100%)', background: 'var(--panel)', border: '1px solid var(--line)',
          borderRadius: 14, padding: narrow ? 18 : 24,
        }}>
          <div style={{ fontFamily: 'var(--display)', fontSize: narrow ? 28 : 32, color: 'var(--gold)' }}>Termix Admin</div>
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>Метрики, клиенты, логи API/движка, деплой</p>
          <input value={email} onChange={(e) => setEmail(e.target.value)} style={inp} placeholder="admin email" autoComplete="username" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={inp} placeholder="password" autoComplete="current-password" />
          {error ? <div style={{ color: 'var(--short)', marginTop: 8, fontSize: 13 }}>{error}</div> : null}
          <button type="submit" style={btn}>Войти</button>
          <div style={{ marginTop: 12 }}><Link to="/">← к терминалу</Link></div>
        </form>
      </div>
    );
  }

  const phase = updateStatus?.deployStatus?.phase;
  const phaseDetail = String(updateStatus?.deployStatus?.detail || '');
  const phaseAt = updateStatus?.deployStatus?.at || null;
  const phaseDone = phase === 'done';
  const phaseFailed = phase === 'failed';
  const phaseColor = phaseDone
    ? 'var(--long)'
    : phaseFailed
      ? 'var(--short)'
      : (deploying || (phase && phase !== 'idle'))
        ? 'var(--gold)'
        : 'var(--muted)';
  const phaseLabel = phaseDone
    ? 'done'
    : phaseFailed
      ? 'failed'
      : phase || (deploying ? 'running…' : 'idle');
  const fmtDeployAt = (iso: string | null | undefined) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    try {
      return d.toLocaleString('ru-RU', { timeZone: 'Asia/Yekaterinburg' }) + ' YEKT';
    } catch {
      return d.toISOString();
    }
  };

  return (
    <div style={{
      height: '100%',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      padding: narrow ? '12px 12px calc(20px + var(--safe-bottom))' : 20,
      maxWidth: 1100, margin: '0 auto',
      boxSizing: 'border-box',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap',
      }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: narrow ? 28 : 36, color: 'var(--gold)' }}>Admin</div>
        <Link to="/" style={{ color: 'var(--muted)' }}>Desk</Link>
        <div style={{
          marginLeft: narrow ? 0 : 'auto', width: narrow ? '100%' : 'auto',
          display: 'flex', gap: 8, flexWrap: 'wrap',
        }}>
          <button
            type="button"
            onClick={() => {
              void adminFetch('/api/admin/migrate', { method: 'POST' })
                .then((r: any) => setDeployOut(`migrate: ${r.ok ? (r.applied || []).join(', ') : r.error}`))
                .then(() => refresh())
                .catch((err) => setDeployOut((err as Error).message));
            }}
            style={{
              ...btn, width: narrow ? 'auto' : 'auto', flex: narrow ? 1 : undefined, marginTop: 0,
              background: 'var(--panel-2)', color: 'var(--gold)', border: '1px solid var(--gold)',
            }}
          >SQL migrate</button>
          <button
            type="button"
            onClick={() => void refresh()}
            style={{ ...btn, width: narrow ? 'auto' : 'auto', flex: narrow ? 1 : undefined, marginTop: 0 }}
          >Обновить</button>
        </div>
      </div>

      <section style={card}>
        <h3 style={h3}>Метрики</h3>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${narrow ? 110 : 140}px,1fr))`, gap: 10 }}>
          {metrics && Object.entries({
            Users: metrics.users,
            Workspaces: metrics.workspaces,
            Keys: metrics.apiKeys,
            Runs: metrics.strategyRuns,
            Armed: metrics.armedRuns,
            Uptime: `${Math.floor(metrics.uptimeSec)}s`,
            Store: metrics.memoryMode ? 'memory' : 'postgres',
            Broker: metrics.brokerId || '—',
            'Σ deposit': `${clientTotals.depositUsdt.toFixed(2)}`,
            'Σ WEEX': `${clientTotals.exchangeEquityUsdt.toFixed(2)}`,
            'Σ volume': `${clientTotals.tradedVolumeUsdt.toFixed(2)}`,
          }).map(([k, v]) => (
            <div key={k} style={{ background: 'var(--panel-2)', borderRadius: 10, padding: 12 }}>
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>{k}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>{v}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={card}>
        <h3 style={h3}>Живые стратегии (engine)</h3>
        <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 0 }}>
          Armed / in_position с деска. Два таба на разные пары и ТФ — нормально при одном ключе.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
            <thead>
              <tr>
                <th style={th}>Symbol</th>
                <th style={th}>TF</th>
                <th style={th}>Kind</th>
                <th style={th}>Status</th>
                <th style={th}>Live</th>
                <th style={th}>Key</th>
              </tr>
            </thead>
            <tbody>
              {engineRuns.map((r) => (
                <tr key={r.id}>
                  <td style={td}>{r.symbol}</td>
                  <td style={td}>{r.timeframe}</td>
                  <td style={td}>{r.kind}</td>
                  <td style={td}>{r.status}</td>
                  <td style={{ ...td, color: r.live ? 'var(--long)' : 'var(--muted)' }}>{r.live ? 'yes' : 'no'}</td>
                  <td style={{ ...td, fontFamily: 'var(--mono)', fontSize: 11 }}>{r.apiKeyId ? `${r.apiKeyId.slice(0, 8)}…` : '—'}</td>
                </tr>
              ))}
              {!engineRuns.length ? (
                <tr><td style={td} colSpan={6}>Нет armed runs</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section style={card}>
        <h3 style={h3}>Клиенты / учёт</h3>
        <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 0 }}>
          Deposit = Aptos на наш счёт. WEEX = equity биржи (только при refresh страницы, без частого опроса).
          Volume / оборачиваемость — за выбранный период (объём / max(WEEX, deposit)).
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
          {([
            ['24h', '24ч'],
            ['7d', '7д'],
            ['30d', '30д'],
            ['all', 'всё'],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              style={{
                ...ghostChip,
                borderColor: clientPeriod === id ? 'var(--gold)' : 'var(--line)',
                color: clientPeriod === id ? 'var(--gold)' : 'var(--text)',
              }}
              onClick={() => {
                setClientPeriod(id);
                void refresh(id).catch((err) => setDeployOut((err as Error).message));
              }}
            >{label}</button>
          ))}
          <button
            type="button"
            style={{ ...ghostChip, marginLeft: 'auto' }}
            onClick={() => void refresh(clientPeriod).catch((err) => setDeployOut((err as Error).message))}
          >Refresh balances</button>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 0 }}>
          Σ deposit: <b style={{ color: 'var(--gold)' }}>{clientTotals.depositUsdt.toFixed(2)}</b>
          {' · '}Σ WEEX: <b style={{ color: 'var(--gold)' }}>{clientTotals.exchangeEquityUsdt.toFixed(2)}</b>
          {' · '}Σ volume ({clientPeriod}): <b style={{ color: 'var(--gold)' }}>{clientTotals.tradedVolumeUsdt.toFixed(2)}</b>
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 780 }}>
            <thead>
              <tr>
                <th style={th}>Email</th>
                <th style={th}>Plan</th>
                <th style={th}>Deposit</th>
                <th style={th}>WEEX</th>
                <th style={th}>Volume</th>
                <th style={th}>Оборот×</th>
                <th style={th}>Создан</th>
                <th style={th}>Действие</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const plan = String(c.planId || 'free').toLowerCase();
                const weex = c.exchangeEquityUsdt;
                return (
                  <tr key={c.id}>
                    <td style={td}>{c.email}</td>
                    <td style={td}>
                      <span style={{
                        padding: '2px 6px', borderRadius: 4, border: '1px solid var(--line)',
                        color: plan === 'free' ? 'var(--muted)' : 'var(--gold)', fontSize: 12,
                      }}>{c.planId || 'free'}</span>
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--mono)' }}>{Number(c.depositUsdt || 0).toFixed(2)}</td>
                    <td style={{ ...td, fontFamily: 'var(--mono)' }}>
                      {weex == null ? '—' : Number(weex).toFixed(2)}
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--mono)' }}>{Number(c.tradedVolumeUsdt || 0).toFixed(2)}</td>
                    <td style={{ ...td, fontFamily: 'var(--mono)' }}>
                      {Number(c.turnover || 0).toFixed(2)}×
                    </td>
                    <td style={{ ...td, color: 'var(--muted)' }}>{c.createdAt ? new Date(c.createdAt).toLocaleString('ru-RU') : '—'}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <select
                          defaultValue={c.planId || 'free'}
                          onChange={(e) => {
                            void adminFetch(`/api/admin/clients/${c.id}/plan`, {
                              method: 'POST',
                              body: JSON.stringify({ planId: e.target.value }),
                            }).then(() => refresh(clientPeriod)).catch((err) => setDeployOut((err as Error).message));
                          }}
                          style={{ ...inp, marginTop: 0, padding: 6, width: 'auto' }}
                        >
                          <option value="free">free</option>
                          <option value="pro">pro</option>
                          <option value="desk">desk</option>
                        </select>
                        <button
                          type="button"
                          style={{ ...btn, width: 'auto', marginTop: 0, padding: '6px 10px', fontSize: 12 }}
                          onClick={() => {
                            const pwd = window.prompt(`Новый пароль для ${c.email} (8+)`, '');
                            if (!pwd || pwd.length < 8) return;
                            void adminFetch(`/api/admin/clients/${c.id}/password`, {
                              method: 'POST',
                              body: JSON.stringify({ password: pwd }),
                            }).then(() => setDeployOut(`Пароль сброшен для ${c.email}`))
                              .catch((err) => setDeployOut((err as Error).message));
                          }}
                        >пароль</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!clients.length ? (
                <tr><td style={td} colSpan={8}>Пока нет пользователей</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section style={card}>
        <h3 style={h3}>Биллинг · Aptos USDT</h3>
        <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 0 }}>
          Депозитные кошельки клиентов и treasury. Env BILLING_* — на VPS; здесь правка treasury/fee.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : 'minmax(0,1fr) 120px auto',
          gap: 8, alignItems: 'end',
        }}>
          <label style={{ fontSize: 12, color: 'var(--muted)', minWidth: 0 }}>
            Treasury address
            <input value={treasuryDraft} onChange={(e) => setTreasuryDraft(e.target.value)} style={{ ...inp, wordBreak: 'break-all' }} placeholder="0x…" />
          </label>
          <label style={{ fontSize: 12, color: 'var(--muted)' }}>
            Fee bps
            <input value={feeDraft} onChange={(e) => setFeeDraft(e.target.value)} style={inp} />
          </label>
          <button
            type="button"
            style={{ ...btn, width: 'auto', marginTop: 0 }}
            onClick={() => {
              void adminFetch('/api/admin/billing/settings', {
                method: 'POST',
                body: JSON.stringify({ treasuryAddress: treasuryDraft, commissionBps: Number(feeDraft) || 0 }),
              }).then(() => refresh()).catch((err) => setDeployOut((err as Error).message));
            }}
          >Сохранить</button>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
          Кошельки: {billing?.wallets?.length ?? 0}
        </div>
        <div style={{ maxHeight: 180, overflow: 'auto', marginTop: 8 }}>
          {(billing?.wallets || []).map((w) => (
            <div key={w.id} style={{
              display: 'grid', gridTemplateColumns: narrow ? '1fr auto' : 'minmax(0,1fr) 80px auto', gap: 8, alignItems: 'center',
              fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--line)',
            }}>
              <div style={{ minWidth: 0 }}>
                <div>{w.email || w.userId.slice(0, 8)}</div>
                <div style={{ fontFamily: 'var(--mono)', color: 'var(--muted)', wordBreak: 'break-all' }}>{w.address}</div>
              </div>
              <div style={{ fontFamily: 'var(--mono)' }}>{Number(w.balanceUsdt || 0).toFixed(2)}</div>
              <button
                type="button"
                style={{ ...btn, width: 'auto', marginTop: 0, padding: '6px 10px', fontSize: 12 }}
                onClick={() => {
                  const amount = Number(window.prompt('Credit USDT amount', '10') || 0);
                  if (!(amount > 0)) return;
                  void adminFetch('/api/admin/billing/credit', {
                    method: 'POST',
                    body: JSON.stringify({ userId: w.userId, amountUsdt: amount }),
                  }).then(() => refresh()).catch((err) => setDeployOut((err as Error).message));
                }}
              >+ credit</button>
            </div>
          ))}
        </div>
      </section>

      <section style={card}>
        <h3 style={h3}>Логи</h3>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {([
            ['all', 'Все'],
            ['api', `API (${logCounts.api})`],
            ['engine', `Движок (${logCounts.engine})`],
            ['deploy', `Деплой (${logCounts.deploy})`],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setLogChannel(id)}
              style={{
                ...ghostChip,
                borderColor: logChannel === id ? 'var(--gold)' : 'var(--line)',
                color: logChannel === id ? 'var(--gold)' : 'var(--muted)',
              }}
            >{label}</button>
          ))}
        </div>
        <div style={{
          fontFamily: 'var(--mono)', fontSize: 12, maxHeight: 260, overflow: 'auto',
          background: '#0a0c0f', padding: 10, borderRadius: 8, border: '1px solid var(--line)',
        }}>
          {logs.map((l, i) => (
            <div key={`${l.ts}-${i}`} style={{
              color: l.level === 'error' ? 'var(--short)' : l.level === 'warn' ? 'var(--gold)' : 'var(--muted)',
              wordBreak: 'break-word', marginBottom: 4,
            }}>
              <span style={{ opacity: 0.7 }}>[{l.ts}]</span>
              {' '}
              <span style={{ color: 'var(--gold)' }}>{l.channel || 'api'}</span>
              {' '}{l.level}: {l.message}
            </div>
          ))}
          {!logs.length ? <div style={{ color: 'var(--muted)' }}>Пока пусто — движок пишет сюда тики enter/exit и ошибки</div> : null}
        </div>
      </section>

      <section style={card}>
        <h3 style={h3}>Настройки окружения</h3>
        <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 0 }}>
          <b style={{ color: 'var(--text)' }}>Только чтение — так и задумано.</b>
          {' '}Секреты и PUBLIC_URL / брокер правятся в <code>.env</code> на VPS, потом restart / «Установить».
          Здесь снимок, чтобы админ видел, с чем реально крутится API.
        </p>
        <div style={{
          display: 'grid', gap: 8,
          maxHeight: 280, overflow: 'auto',
          border: '1px solid var(--line)', borderRadius: 10, padding: 10, background: 'var(--panel-2)',
        }}>
          {Object.entries(settings).map(([k, v]) => (
            <div key={k} style={{
              display: 'grid',
              gridTemplateColumns: narrow ? '1fr' : 'minmax(120px,180px) minmax(0,1fr)',
              gap: narrow ? 2 : 10, fontSize: 12,
            }}>
              <div style={{ color: 'var(--gold)', fontFamily: 'var(--mono)' }}>{k}</div>
              <div style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{v || '—'}</div>
            </div>
          ))}
          {!Object.keys(settings).length ? <div style={{ color: 'var(--muted)' }}>пусто</div> : null}
        </div>
      </section>

      <section style={{
        ...card,
        borderColor: updateStatus?.updateAvailable ? 'rgba(201,162,39,0.65)' : 'var(--line)',
        boxShadow: updateStatus?.updateAvailable ? '0 0 0 1px rgba(201,162,39,0.25)' : 'none',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <h3 style={{ ...h3, margin: 0 }}>{t.adminUpdateTitle}</h3>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 12px', borderRadius: 8, fontWeight: 800, fontSize: 13, letterSpacing: 0.04,
            background: updateStatus?.updateAvailable ? 'var(--gold)' : 'rgba(61,186,122,0.18)',
            color: updateStatus?.updateAvailable ? '#111' : 'var(--long)',
            border: updateStatus?.updateAvailable ? 'none' : '1px solid rgba(61,186,122,0.35)',
          }}>
            {updateStatus?.updateAvailable ? `◆ ${t.adminUpdateAvailable}` : `● ${t.adminUpdateCurrent}`}
          </span>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
          github.com/aleksey-34/Termix → /var/www/termix → build → restart → <b>done</b>.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 12 }}>
          <div style={{ background: 'var(--panel-2)', borderRadius: 10, padding: 12 }}>
            <div style={{ color: 'var(--muted)', fontSize: 12 }}>VPS</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 18 }}>{updateStatus?.deployedSha || '—'}</div>
            <div style={{ color: 'var(--muted)', fontSize: 11, wordBreak: 'break-all' }}>
              {fmtDeployAt(updateStatus?.deployedAt) || updateStatus?.deployedAt || ''}
            </div>
          </div>
          <div style={{ background: 'var(--panel-2)', borderRadius: 10, padding: 12 }}>
            <div style={{ color: 'var(--muted)', fontSize: 12 }}>GitHub main</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 18, color: updateStatus?.updateAvailable ? 'var(--gold)' : 'var(--text)' }}>
              {updateStatus?.githubMain || updateStatus?.availableSha || '—'}
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 11, wordBreak: 'break-all' }}>
              {updateStatus?.githubSource || ''}
            </div>
          </div>
          <div style={{ background: 'var(--panel-2)', borderRadius: 10, padding: 12 }}>
            <div style={{ color: 'var(--muted)', fontSize: 12 }}>deploy</div>
            <div style={{ color: phaseColor, fontSize: 14, marginTop: 2, fontFamily: 'var(--mono)', fontWeight: 700 }}>
              {phaseLabel}
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4, wordBreak: 'break-word' }}>
              {phaseDone
                ? `завершён${phaseAt ? ` · ${fmtDeployAt(phaseAt)}` : ''}`
                : phaseFailed
                  ? (phaseDetail || 'ошибка')
                  : (phaseDetail || (deploying ? 'идёт установка…' : ''))}
            </div>
          </div>
        </div>
        {updateStatus?.updateAvailable ? (
          <div style={{
            marginBottom: 12, padding: 12, borderRadius: 10,
            background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.35)',
          }}>
            <div style={{ fontWeight: 700, color: 'var(--gold)', marginBottom: 8, fontSize: 13 }}>
              {t.adminUpdateWhatsNew}
              {updateStatus.changelogSource ? (
                <span style={{ color: 'var(--muted)', fontWeight: 500, fontSize: 11, marginLeft: 8 }}>
                  {updateStatus.changelogSource}
                </span>
              ) : null}
            </div>
            {updateStatus.changelog?.length ? (
              <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 220, overflow: 'auto' }}>
                {updateStatus.changelog.map((c) => (
                  <li key={`chg-${c.sha}-${c.n || 0}`} style={{ marginBottom: 6, fontSize: 13, lineHeight: 1.35 }}>
                    <code style={{ color: 'var(--gold)', fontFamily: 'var(--mono)', fontSize: 12 }}>{c.sha}</code>
                    {' '}{c.message}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>{t.adminUpdateNoNotes}</div>
            )}
          </div>
        ) : null}
        <div style={{
          marginBottom: 12, padding: 12, borderRadius: 10,
          background: 'var(--panel-2)', border: '1px solid var(--line)',
        }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 8, fontSize: 13 }}>
            {t.adminUpdateHistory}
            {updateStatus?.historySource ? (
              <span style={{ color: 'var(--muted)', fontWeight: 500, fontSize: 11, marginLeft: 8 }}>
                {updateStatus.historySource}
              </span>
            ) : null}
          </div>
          {updateStatus?.history?.length ? (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 320, overflow: 'auto' }}>
              {updateStatus.history.map((c) => {
                const key = `${c.sha}-${c.n || 0}`;
                const open = openHistorySha === key;
                const hasBody = Boolean(c.body && c.body.trim());
                return (
                  <li key={key} style={{
                    borderBottom: '1px solid var(--line)',
                    padding: '8px 0',
                  }}>
                    <button
                      type="button"
                      onClick={() => setOpenHistorySha(open ? null : key)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '28px 64px minmax(0,1fr) auto',
                        gap: 8,
                        width: '100%',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--text)',
                        cursor: hasBody || true ? 'pointer' : 'default',
                        padding: 0,
                        fontSize: 13,
                        lineHeight: 1.35,
                        alignItems: 'start',
                      }}
                      title={hasBody ? t.adminUpdateHistoryExpand : undefined}
                    >
                      <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 12 }}>
                        {c.n ?? '·'}
                      </span>
                      <code style={{ color: 'var(--gold)', fontFamily: 'var(--mono)', fontSize: 12 }}>{c.sha}</code>
                      <span style={{ overflowWrap: 'anywhere' }}>{c.message}</span>
                      <span style={{ color: 'var(--muted)', fontSize: 11, whiteSpace: 'nowrap' }}>
                        {open ? '▾' : '▸'}
                      </span>
                    </button>
                    {open ? (
                      <div style={{
                        marginTop: 6, marginLeft: 36, padding: '8px 10px',
                        borderRadius: 8, background: '#0a0c0f', border: '1px solid var(--line)',
                        color: 'var(--muted)', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        fontFamily: 'var(--mono)',
                      }}>
                        {hasBody ? c.body : t.adminUpdateHistoryNoBody}
                        {c.date ? (
                          <div style={{ marginTop: 8, color: 'var(--muted)', opacity: 0.85 }}>
                            {fmtDeployAt(c.date)}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>{t.adminUpdateHistoryEmpty}</div>
          )}
        </div>
        {updateStatus?.statusReason ? (
          <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0, wordBreak: 'break-word' }}>
            {updateStatus.statusReason}
          </p>
        ) : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button disabled={busy || deploying} onClick={() => void checkUpdates()} style={{ ...btn, width: 'auto', marginTop: 0, opacity: busy || deploying ? 0.6 : 1 }}>
            {t.adminUpdateCheck}
          </button>
          <button disabled={busy || deploying} onClick={() => void deploy()} style={{ ...btn, width: 'auto', marginTop: 0, opacity: busy || deploying ? 0.6 : 1 }}>
            {deploying ? t.adminUpdateInstalling : t.adminUpdateInstall}
          </button>
        </div>
        <pre style={{
          marginTop: 10, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          color: 'var(--muted)', background: '#0a0c0f', border: '1px solid var(--line)',
          borderRadius: 8, padding: 10, maxHeight: 200, overflow: 'auto',
        }}>{deployOut || '—'}</pre>
        {!updateStatus?.wrapperOk ? (
          <p style={{ color: 'var(--gold)', fontSize: 12, lineHeight: 1.45 }}>
            Permission denied → VPS:
            <br />
            <code style={{ color: 'var(--gold)', wordBreak: 'break-all' }}>{updateStatus?.oneShot || 'sudo bash /var/www/termix/scripts/remote-apply.sh'}</code>
          </p>
        ) : null}
      </section>
    </div>
  );
}

const inp: React.CSSProperties = { width: '100%', marginTop: 8, padding: 10, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--text)', boxSizing: 'border-box' };
const btn: React.CSSProperties = { marginTop: 12, width: '100%', padding: 10, borderRadius: 8, border: 'none', background: 'var(--gold)', color: '#111', fontWeight: 700, cursor: 'pointer' };
const ghostChip: React.CSSProperties = { padding: '4px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--panel-2)', cursor: 'pointer', fontSize: 12 };
const card: React.CSSProperties = {
  background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 12,
  padding: 16, marginBottom: 14, overflow: 'visible',
};
const h3: React.CSSProperties = { margin: '0 0 10px', fontSize: 16 };
const th: React.CSSProperties = { textAlign: 'left', borderBottom: '1px solid var(--line)', padding: 6, color: 'var(--muted)' };
const td: React.CSSProperties = { borderBottom: '1px solid var(--line)', padding: 6 };
